/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { ReactElement, useEffect, useReducer } from "react";

import { Section } from "react-bulma-components";
import { useParams } from "react-router-dom";

import { getPreparedRouteByAPIResponse } from "api/prepared_routes";
import { Navbar, RoutesTable, SystemNotification } from "components";
import { GlobalErrorScreen } from "components/GlobalErrorScreen";
import { PreparedRoutesByWorkplace } from "containers/Routes/Routes";
import {
  useGetApproverUsersUserIdPreparedRouteApi,
  useGetApproverUsersUserIdPreparedRoutesApi,
} from "hooks/api";
import { PreparedRoute, PreparedRouteApproval } from "models/preparedRoute";

interface ApplicantRoutesState {
  preparedRoutes: PreparedRoute[];
  preparedRoutesByWorkplaces: PreparedRoutesByWorkplace[];
  otherPreparedRoutes: PreparedRoute[];
  otherPreparedRoutesByWorkplaces: PreparedRoutesByWorkplace[];
  isPreparedRoutesLoaded: boolean;
}

const initialState: ApplicantRoutesState = {
  preparedRoutes: [],
  preparedRoutesByWorkplaces: [],
  otherPreparedRoutes: [],
  otherPreparedRoutesByWorkplaces: [],
  isPreparedRoutesLoaded: false,
};

interface SetIsPreparedRoutesLoadedAction {
  type: "SET_IS_PREPARED_ROUTES_LOADED";
  payload: {
    isPreparedRoutesLoaded: boolean;
  };
}

interface SetOtherPreparedRoutesAction {
  type: "SET_OTHER_PREPARED_ROUTES";
  payload: {
    otherPreparedRoutes: PreparedRoute[];
  };
}

interface SetPreparedRoutesAction {
  type: "SET_PREPARED_ROUTES";
  payload: {
    preparedRoutes: PreparedRoute[];
  };
}

type RoutesActions =
  | SetIsPreparedRoutesLoadedAction
  | SetOtherPreparedRoutesAction
  | SetPreparedRoutesAction;

const getPreparedRoutesByWorkplaces = (preparedRoutes: PreparedRoute[]) => {
  const preparedRoutesByWorkplaces: PreparedRoutesByWorkplace[] = [];
  // eslint-disable-next-line no-restricted-syntax
  for (const preparedRoute of preparedRoutes) {
    let found = false;
    // eslint-disable-next-line no-restricted-syntax
    for (const preparedRoutesByWorkplace of preparedRoutesByWorkplaces) {
      if (preparedRoutesByWorkplace.arrivalId === preparedRoute.arrivalId) {
        preparedRoutesByWorkplace.preparedRoutes.push(preparedRoute);
        found = true;
        break;
      }
    }
    if (!found) {
      preparedRoutesByWorkplaces.push({
        arrivalId: preparedRoute.arrivalId,
        arrivalName: preparedRoute.arrivalName,
        departureId: preparedRoute.departureId,
        departureName: preparedRoute.departureName,
        preparedRoutes: [preparedRoute],
      });
    }
  }

  return preparedRoutesByWorkplaces;
};

export const getPreparedRouteApprovalByAPIResponse = (
  preparedRouteApproval: any
) =>
  ({
    id: preparedRouteApproval.id,
    preparedRouteId: preparedRouteApproval.prepared_route_id,
    applicantComment: preparedRouteApproval.applicant_comment || "",
    approvalStatus: preparedRouteApproval.approval_status,
    approvalStatusName: preparedRouteApproval.approval_status_name,
    approverComment: preparedRouteApproval.approver_comment,
  } as PreparedRouteApproval);

const reducer: React.Reducer<ApplicantRoutesState, RoutesActions> = (
  currentState,
  action
) => {
  const newState: ApplicantRoutesState = { ...currentState };

  switch (action.type) {
    case "SET_IS_PREPARED_ROUTES_LOADED":
      newState.isPreparedRoutesLoaded = action.payload.isPreparedRoutesLoaded;
      break;
    case "SET_OTHER_PREPARED_ROUTES":
      newState.otherPreparedRoutes = action.payload.otherPreparedRoutes;
      newState.otherPreparedRoutesByWorkplaces = getPreparedRoutesByWorkplaces(
        action.payload.otherPreparedRoutes
      );
      break;
    case "SET_PREPARED_ROUTES":
      newState.preparedRoutes = action.payload.preparedRoutes;
      newState.preparedRoutesByWorkplaces = getPreparedRoutesByWorkplaces(
        action.payload.preparedRoutes
      );
      break;
    default:
      throw new Error();
  }
  return newState;
};

interface ApplicantRoutesProps {
  id?: string;
  userId?: string;
}

export function ApplicantRoutesContent(): ReactElement | null {
  const { id, userId } = useParams<ApplicantRoutesProps>();
  const [state, dispatch] = useReducer(reducer, initialState);

  const getApproverUsersUserIdPreparedRouteApi =
    useGetApproverUsersUserIdPreparedRouteApi();
  const getApproverUsersUserIdPreparedRoutesApi =
    useGetApproverUsersUserIdPreparedRoutesApi();

  useEffect(() => {
    (async () => {
      try {
        const preparedRoutes = [
          getPreparedRouteByAPIResponse(
            // FIXME: ここuserIdとidがundefinedも許可しているけどapi的にはやめた方が良いと思う
            await getApproverUsersUserIdPreparedRouteApi(userId, id)
          ),
        ];

        // FIXME: ここuserIdとidがundefinedも許可しているけどapi的にはやめた方が良いと思う
        const otherPreparedRoutes = (
          await getApproverUsersUserIdPreparedRoutesApi(userId)
        )
          .reduce(
            (acc: PreparedRoute[], preparedRoute: any) => [
              ...acc,
              getPreparedRouteByAPIResponse(preparedRoute),
            ],
            []
          )
          .filter(
            (preparedRoute: PreparedRoute) =>
              preparedRoute.id !== preparedRoutes[0].id
          );

        dispatch({
          type: "SET_PREPARED_ROUTES",
          payload: {
            preparedRoutes,
          },
        });

        dispatch({
          type: "SET_OTHER_PREPARED_ROUTES",
          payload: {
            otherPreparedRoutes,
          },
        });
      } catch (error) {
        // eslint-disable-next-line no-console
        console.log(error);
      } finally {
        dispatch({
          type: "SET_IS_PREPARED_ROUTES_LOADED",
          payload: {
            isPreparedRoutesLoaded: true,
          },
        });
      }
    })();
  }, [
    id,
    userId,
    getApproverUsersUserIdPreparedRoutesApi,
    getApproverUsersUserIdPreparedRouteApi,
  ]);

  return (
    <>
      <header>
        <Navbar />
      </header>
      <main role="main" className="container">
        <Section>
          <SystemNotification />
          <div className="cw-routes-header">
            <h2 className="title is-2 cw-routes-header-title">
              申請された通勤経路
            </h2>
          </div>
          <RoutesTable
            preparedRoutesByWorkplaces={state.preparedRoutesByWorkplaces}
            isPreparedRoutesLoaded={state.isPreparedRoutesLoaded}
            isUpdatePreparedRoutesInProgress={false}
            isApprover
          />

          <div className="cw-routes-header">
            <h2 className="title is-2 cw-routes-header-title">
              その他の通勤経路
            </h2>
          </div>
          <RoutesTable
            preparedRoutesByWorkplaces={state.otherPreparedRoutesByWorkplaces}
            isPreparedRoutesLoaded={state.isPreparedRoutesLoaded}
            isUpdatePreparedRoutesInProgress={false}
            isApprover
          />
        </Section>
      </main>
    </>
  );
}

export function ApplicantRoutes(): ReactElement | null {
  return (
    <GlobalErrorScreen>
      <ApplicantRoutesContent />
    </GlobalErrorScreen>
  );
}
/* eslint-enable */
